// Sugalarm — /api/members + /api/me/members + /api/me/active_member (cycle 12).
//
// Cycle 13 added the per-member CGM-link wrappers (`connectCgm`,
// `disconnectCgm`) that the Household screen uses to wire each member's
// own Dexcom or Libre account.

import { request } from "../lib/apiClient";
import type { CGMConnectIn, CGMConnectResult, CGMStatus } from "./cgm";
import type {
  ActiveMember,
  ApiResult,
  Member,
  MemberDashboardResponse,
  MemberListResponse,
  SensorPause,
} from "../lib/types";

export interface MemberCreateIn {
  display_name: string;
  relationship_label?: string | null;
}

export interface MemberPatchIn {
  display_name?: string;
  relationship_label?: string | null;
}

// ─── /api/members ───────────────────────────────────────────────────────

export function list(
  includeArchived = false,
): Promise<ApiResult<MemberListResponse>> {
  const qs = includeArchived ? "?include_archived=true" : "";
  return request<MemberListResponse>(`/api/members${qs}`);
}

export function get(memberId: string): Promise<ApiResult<Member>> {
  return request<Member>(`/api/members/${memberId}`);
}

export function create(body: MemberCreateIn): Promise<ApiResult<Member>> {
  return request<Member>("/api/members", { method: "POST", body });
}

export function patch(
  memberId: string,
  body: MemberPatchIn,
): Promise<ApiResult<Member>> {
  return request<Member>(`/api/members/${memberId}`, {
    method: "PATCH",
    body,
  });
}

export function archive(memberId: string): Promise<ApiResult<void>> {
  return request<void>(`/api/members/${memberId}/archive`, {
    method: "POST",
  });
}

// ─── /api/me/members ────────────────────────────────────────────────────

// Cycle 233.2: grantee-side unlink — drop a member shared TO the caller
// from their accessible set (the inverse of the owner-only
// DELETE /api/members/{id}/link/{user_id}; takes no user_id, always
// operates on the caller, refuses a member the caller owns → 404).
export function unlinkOwnGrant(memberId: string): Promise<ApiResult<void>> {
  return request<void>(`/api/me/members/${memberId}/link`, {
    method: "DELETE",
  });
}

export function listMine(
  includeArchived = false,
): Promise<ApiResult<MemberListResponse>> {
  // Cycle 60: owned ∪ granted (the dashboard / active-member switcher set).
  // Cycle 113: optional `include_archived` so Settings → Members renders
  // this same accessible set while keeping its "Show archived" toggle —
  // the owner-only endpoint (`list`) is no longer the Members pane source.
  const qs = includeArchived ? "?include_archived=true" : "";
  return request<MemberListResponse>(`/api/me/members${qs}`);
}

// Cycle 433: abortable — part of the bounded wake reseed.
export function dashboard(
  signal?: AbortSignal,
): Promise<ApiResult<MemberDashboardResponse>> {
  return request<MemberDashboardResponse>(
    "/api/me/members?with_latest=true",
    { signal },
  );
}

// ─── /api/me/active_member ──────────────────────────────────────────────

export function getActive(): Promise<ApiResult<ActiveMember>> {
  return request<ActiveMember>("/api/me/active_member");
}

export function setActive(
  member_id: string | null,
): Promise<ApiResult<ActiveMember>> {
  return request<ActiveMember>("/api/me/active_member", {
    method: "PUT",
    body: { member_id },
  });
}

// ─── Per-member CGM linking (cycle 13) ──────────────────────────────────
// Same wire shape as `cgmApi.connect`/`disconnect`/`status`, just keyed
// off a specific member id rather than the active member.

export function connectCgm(
  memberId: string,
  body: CGMConnectIn,
): Promise<ApiResult<CGMConnectResult>> {
  return request<CGMConnectResult>(`/api/members/${memberId}/cgm/connect`, {
    method: "POST",
    body,
  });
}

export function cgmStatus(memberId: string): Promise<ApiResult<CGMStatus>> {
  return request<CGMStatus>(`/api/members/${memberId}/cgm/status`);
}

export function disconnectCgm(memberId: string): Promise<ApiResult<void>> {
  return request<void>(`/api/members/${memberId}/cgm/disconnect`, {
    method: "DELETE",
  });
}

// ─── Cycle 354: sensor-pause hold (blackout-page snooze) ────────────────

export function setSensorPause(
  memberId: string,
  reason: "sensor_change" | "away",
  durationMinutes?: number,
): Promise<ApiResult<{ sensor_pause: SensorPause }>> {
  return request<{ sensor_pause: SensorPause }>(
    `/api/members/${memberId}/sensor_pause`,
    {
      method: "POST",
      body: {
        reason,
        ...(durationMinutes ? { duration_minutes: durationMinutes } : {}),
      },
    },
  );
}

export function clearSensorPause(
  memberId: string,
): Promise<ApiResult<void>> {
  return request<void>(`/api/members/${memberId}/sensor_pause`, {
    method: "DELETE",
  });
}
